





















































































































































































import { Component, Prop, PropSync, Vue } from "vue-property-decorator";
import { Content } from "@/interfaces/content";
import { TenantApiClient } from "@/api/tenant_client";
import { Uppy } from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import CardComponent from "@/components/CardComponent.vue";
import CloudflarePlayer from "@/components/CloudflarePlayer.vue";
import DragDrop from "@uppy/drag-drop";
import RevertableInput from "@/components/RevertableInput.vue";
import StatusBar from "@uppy/status-bar";

import "@uppy/core/dist/style.css";
import "@uppy/drag-drop/dist/style.css";
import "@uppy/file-input/dist/style.css";
import "@uppy/status-bar/dist/style.css";

interface Upload {
  file?: File;
  isLoading: boolean;
  accept: string;
}

@Component({
  components: {
    CardComponent,
    RevertableInput,
    CloudflarePlayer
  }
})
export default class VideoContent extends Vue {
  @Prop() tenantId!: string;
  @Prop() contentId!: string;
  @Prop() original?: any;
  @PropSync("content") syncedContent!: any;

  // Other Variables
  private api = new TenantApiClient();
  updateInterval: any = undefined;
  tags: string[] = [];

  public thumbnailUpload = {
    file: undefined,
    isLoading: false,
    accept: "image/*"
  } as Upload;

  public videoUpload = {
    file: undefined,
    isLoading: false,
    accept: "video/*"
  } as Upload;

  async mounted() {
    const uppy = new Uppy({
      id: `uppy-${this.contentId}`,
      autoProceed: true,
      allowMultipleUploads: false,
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ["video/*"]
      },
      meta: {
        tenantId: this.tenantId,
        contentId: this.contentId,
        apiUrl: this.api.api
      }
    });

    uppy.use(StatusBar, {
      id: `uppy-statusbar-${this.contentId}`,
      target: "#video-upload-status",
      hideAfterFinish: true,
      showProgressDetails: true,
      hideUploadButton: false,
      hideCancelButton: false,
      hidePauseResumeButton: false
    });

    uppy.use(DragDrop, {
      id: `uppy-fileinput-${this.contentId}`,
      target: "#video-upload-button"
    });

    uppy.use(AwsS3, {
      id: `uppy-aws-${this.contentId}`,
      companionUrl: "https://uppy.eljot-services.com",
      metaFields: ["tenantId", "contentId", "apiUrl"]
    });

    await this.updateVideoMeta();

    this.updateInterval = setInterval(async () => {
      await this.updateVideoMeta();
    }, 5000);
  }

  beforeDestroy() {
    clearInterval(this.updateInterval);
  }

  async updateVideoMeta() {
    if (this.tenantId && this.contentId) {
      const [tags, content]: [string[], any] = await Promise.all([
        this.api.getTags(this.tenantId, "video"),
        this.api.getContent(this.tenantId, this.contentId)
      ]);

      this.tags = tags;

      ["is_video_ready_to_play", "video_duration"].forEach((prop: string) => {
        if (content[prop] !== this.syncedContent[prop]) {
          console.debug(
            `Found difference in ${prop}: ${content[prop]} vs. ${this.syncedContent[prop]}`
          );
          this.syncedContent[prop] = content[prop];
        }
      });
    }
  }

  async uploadImage(file: File) {
    let formData = new FormData();
    formData.append("file", file);

    this.thumbnailUpload.isLoading = true;

    try {
      const newContent: Content = await this.api.uploadThumbnail(
        this.tenantId,
        this.contentId,
        formData
      );
      if (this.original) {
        this.original = newContent;
      }

      this.syncedContent.thumbnail_url = newContent.thumbnail_url;
    } finally {
      this.thumbnailUpload.file = undefined;
      this.thumbnailUpload.isLoading = false;
    }
  }

  async uploadVideo(file: File) {
    if (file.size / 1024 ** 3 >= 5) {
      // Right now, B2 has an upload limit of 5GB that we need to check against.
      this.$buefy.toast.open({
        message: this.$t("upload.file_size_issue"),
        type: "is-danger",
        queue: false
      });

      this.thumbnailUpload.file = undefined;
      return;
    }

    let formData = new FormData();
    formData.append("file", file);

    this.videoUpload.isLoading = true;

    try {
      const newContent: Content = await this.api.uploadVideo(
        this.tenantId,
        this.contentId,
        formData
      );

      if (this.original) {
        this.original = newContent;
      }
      this.syncedContent.video_url = newContent.video_url;
      this.syncedContent.internal_video_url = newContent.internal_video_url;
      this.syncedContent.cloudflare_video_id = newContent.cloudflare_video_id;
      this.syncedContent.signed_cloudflare_video_id =
        newContent.signed_cloudflare_video_id;
      this.syncedContent.video_duration = newContent.video_duration;
      this.syncedContent.is_video_ready_to_play =
        newContent.is_video_ready_to_play;
    } finally {
      this.videoUpload.file = undefined;
      this.videoUpload.isLoading = false;
    }
  }
}
